import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { useDispatch } from 'react-redux';
import { addLead } from '../redux/slices/leadSlice';
import * as mockDataService from '../services/mockData';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface LeadImporterProps {
  open: boolean;
  onClose: () => void;
  products: Array<{ id: string; name: string; price: number }>;
}

interface ImportedLead {
  name: string;
  email: string;
  phone: string;
  source: string;
  status: string;
  notes: string;
  productInterest: string;
  isValid: boolean;
  errors: string[];
}

const LeadImporter: React.FC<LeadImporterProps> = ({ open, onClose, products }) => {
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [fileType, setFileType] = useState<string>('csv');
  const [loading, setLoading] = useState(false);
  const [importedLeads, setImportedLeads] = useState<ImportedLead[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [defaultSource, setDefaultSource] = useState('Importação');
  const [defaultStatus, setDefaultStatus] = useState('Novo');
  const [defaultProduct, setDefaultProduct] = useState('');

  const steps = ['Selecionar arquivo', 'Revisar dados', 'Importar leads'];

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
      setError(null);
    }
  };

  const handleFileTypeChange = (event: SelectChangeEvent) => {
    setFileType(event.target.value);
  };

  const handleDefaultSourceChange = (event: SelectChangeEvent) => {
    setDefaultSource(event.target.value);
  };

  const handleDefaultStatusChange = (event: SelectChangeEvent) => {
    setDefaultStatus(event.target.value);
  };

  const handleDefaultProductChange = (event: SelectChangeEvent) => {
    setDefaultProduct(event.target.value);
  };

  const parseCSV = (text: string): ImportedLead[] => {
    const lines = text.split('\n');
    if (lines.length < 2) {
      throw new Error('O arquivo não contém dados suficientes');
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const nameIndex = headers.indexOf('nome') !== -1 ? headers.indexOf('nome') : headers.indexOf('name');
    const emailIndex = headers.indexOf('email') !== -1 ? headers.indexOf('email') : headers.indexOf('e-mail');
    const phoneIndex = headers.indexOf('telefone') !== -1 ? headers.indexOf('telefone') : headers.indexOf('phone');
    const notesIndex = headers.indexOf('observações') !== -1 ? headers.indexOf('observações') : headers.indexOf('notes');

    if (nameIndex === -1 || emailIndex === -1) {
      throw new Error('O arquivo deve conter pelo menos as colunas Nome e Email');
    }

    const parsedLeads: ImportedLead[] = [];

    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;

      const values = lines[i].split(',').map(v => v.trim());
      const lead: ImportedLead = {
        name: values[nameIndex] || '',
        email: values[emailIndex] || '',
        phone: phoneIndex !== -1 ? values[phoneIndex] || '' : '',
        source: defaultSource,
        status: defaultStatus,
        notes: notesIndex !== -1 ? values[notesIndex] || '' : '',
        productInterest: defaultProduct,
        isValid: true,
        errors: [],
      };

      // Validação básica
      if (!lead.name) {
        lead.isValid = false;
        lead.errors.push('Nome é obrigatório');
      }

      if (!lead.email) {
        lead.isValid = false;
        lead.errors.push('Email é obrigatório');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email)) {
        lead.isValid = false;
        lead.errors.push('Email inválido');
      }

      parsedLeads.push(lead);
    }

    return parsedLeads;
  };

  const parseExcel = async (file: File): Promise<ImportedLead[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          if (!data) {
            reject(new Error('Erro ao ler o arquivo'));
            return;
          }
          
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Converter para JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          if (jsonData.length === 0) {
            reject(new Error('O arquivo não contém dados'));
            return;
          }
          
          // Mapear os dados para o formato de leads
          const parsedLeads: ImportedLead[] = jsonData.map((row: any) => {
            const lead: ImportedLead = {
              name: row.Nome || row.nome || row.Name || row.name || '',
              email: row.Email || row.email || row['E-mail'] || row['e-mail'] || '',
              phone: row.Telefone || row.telefone || row.Phone || row.phone || '',
              source: defaultSource,
              status: defaultStatus,
              notes: row.Observações || row.observações || row.Notes || row.notes || '',
              productInterest: defaultProduct,
              isValid: true,
              errors: [],
            };
            
            // Validação básica
            if (!lead.name) {
              lead.isValid = false;
              lead.errors.push('Nome é obrigatório');
            }
            
            if (!lead.email) {
              lead.isValid = false;
              lead.errors.push('Email é obrigatório');
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email)) {
              lead.isValid = false;
              lead.errors.push('Email inválido');
            }
            
            return lead;
          });
          
          resolve(parsedLeads);
        } catch (error) {
          reject(new Error('Erro ao processar arquivo Excel'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Erro ao ler o arquivo'));
      };
      
      reader.readAsBinaryString(file);
    });
  };

  const handleProcessFile = async () => {
    if (!file) {
      setError('Por favor, selecione um arquivo');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let leads: ImportedLead[] = [];

      if (fileType === 'csv') {
        const text = await file.text();
        leads = parseCSV(text);
      } else if (fileType === 'excel') {
        leads = await parseExcel(file);
      } else {
        throw new Error('Formato de arquivo não suportado');
      }

      if (leads.length === 0) {
        throw new Error('Nenhum lead encontrado no arquivo');
      }

      setImportedLeads(leads);
      setActiveStep(1);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const validLeads = importedLeads.filter(lead => lead.isValid);
      
      if (validLeads.length === 0) {
        throw new Error('Não há leads válidos para importar');
      }

      // Processar os leads em sequência para evitar problemas de concorrência
      for (const lead of validLeads) {
        try {
          // Primeiro, salvar no localStorage usando o mockDataService
          const newLead = {
            name: lead.name,
            email: lead.email,
            phone: lead.phone,
            source: lead.source,
            status: lead.status,
            notes: lead.notes,
            lastContactDate: new Date().toISOString(),
            createdAt: new Date().toISOString(),
            productInterest: lead.productInterest || undefined,
          };
          
          // Criar o lead no localStorage
          const createdLead = await mockDataService.createLead(newLead);
          
          // Depois, atualizar o estado no Redux
          dispatch(addLead(createdLead));
        } catch (leadError) {
          console.error('Erro ao importar lead:', leadError);
          // Continuar com o próximo lead mesmo se houver erro
        }
      }

      setSuccess(`${validLeads.length} leads importados com sucesso!`);
      setActiveStep(2);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setImportedLeads([]);
    setError(null);
    setSuccess(null);
    setActiveStep(0);
    onClose();
  };

  const handleReset = () => {
    setFile(null);
    setImportedLeads([]);
    setError(null);
    setSuccess(null);
    setActiveStep(0);
  };

  const downloadTemplateCSV = () => {
    const headers = 'Nome,Email,Telefone,Observações\n';
    const exampleRow = 'João Silva,joao.silva@exemplo.com,(11) 98765-4321,"Interessado em nossos produtos"\n';
    const blob = new Blob([headers, exampleRow], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, 'template_leads.csv');
  };

  const downloadTemplateExcel = () => {
    // Criar uma planilha
    const worksheet = XLSX.utils.json_to_sheet([
      {
        Nome: 'João Silva',
        Email: 'joao.silva@exemplo.com',
        Telefone: '(11) 98765-4321',
        Observações: 'Interessado em nossos produtos'
      }
    ]);
    
    // Criar um workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads');
    
    // Gerar o arquivo
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'template_leads.xlsx');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Importar Leads</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Selecione o arquivo para importar
            </Typography>

            <FormControl fullWidth margin="normal">
              <InputLabel id="file-type-label">Tipo de Arquivo</InputLabel>
              <Select
                labelId="file-type-label"
                value={fileType}
                onChange={handleFileTypeChange}
                label="Tipo de Arquivo"
              >
                <MenuItem value="csv">CSV (.csv)</MenuItem>
                <MenuItem value="excel">Excel (.xls, .xlsx)</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                startIcon={<DownloadIcon />}
                onClick={fileType === 'csv' ? downloadTemplateCSV : downloadTemplateExcel}
                color="secondary"
              >
                Baixar Template
              </Button>
            </Box>

            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 3,
                mt: 2,
                mb: 3,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              }}
              component="label"
            >
              <input
                type="file"
                accept={fileType === 'csv' ? '.csv' : '.xls,.xlsx'}
                hidden
                onChange={handleFileChange}
              />
              <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="body1" gutterBottom>
                Clique para selecionar ou arraste o arquivo aqui
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {fileType === 'csv'
                  ? 'Arquivos CSV devem ter cabeçalhos: Nome, Email, Telefone, Observações'
                  : 'Suporta arquivos Excel (.xls, .xlsx)'}
              </Typography>
              {file && (
                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                  Arquivo selecionado: {file.name}
                </Typography>
              )}
            </Box>

            <Typography variant="h6" gutterBottom>
              Configurações padrão
            </Typography>

            <FormControl fullWidth margin="normal">
              <InputLabel id="default-source-label">Origem Padrão</InputLabel>
              <Select
                labelId="default-source-label"
                value={defaultSource}
                onChange={handleDefaultSourceChange}
                label="Origem Padrão"
              >
                <MenuItem value="Importação">Importação</MenuItem>
                <MenuItem value="Site">Site</MenuItem>
                <MenuItem value="Facebook">Facebook</MenuItem>
                <MenuItem value="Instagram">Instagram</MenuItem>
                <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                <MenuItem value="Twitter">Twitter</MenuItem>
                <MenuItem value="WhatsApp">WhatsApp</MenuItem>
                <MenuItem value="Indicação">Indicação</MenuItem>
                <MenuItem value="Outro">Outro</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel id="default-status-label">Status Padrão</InputLabel>
              <Select
                labelId="default-status-label"
                value={defaultStatus}
                onChange={handleDefaultStatusChange}
                label="Status Padrão"
              >
                <MenuItem value="Novo">Novo</MenuItem>
                <MenuItem value="Contatado">Contatado</MenuItem>
                <MenuItem value="Qualificado">Qualificado</MenuItem>
                <MenuItem value="Convertido">Convertido</MenuItem>
                <MenuItem value="Perdido">Perdido</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel id="default-product-label">Produto de Interesse Padrão</InputLabel>
              <Select
                labelId="default-product-label"
                value={defaultProduct}
                onChange={handleDefaultProductChange}
                label="Produto de Interesse Padrão"
              >
                <MenuItem value="">Nenhum</MenuItem>
                {products.map((product) => (
                  <MenuItem key={product.id} value={product.id}>
                    {product.name} - R$ {product.price.toFixed(2)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Revisar dados antes de importar
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Encontramos {importedLeads.length} leads no arquivo. 
              {importedLeads.filter(l => !l.isValid).length > 0 && 
                ` (${importedLeads.filter(l => !l.isValid).length} com erros)`}
            </Typography>

            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Telefone</TableCell>
                    <TableCell>Origem</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Produto</TableCell>
                    <TableCell>Válido</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importedLeads.map((lead, index) => {
                    const product = products.find(p => p.id === lead.productInterest);
                    
                    return (
                      <TableRow 
                        key={index}
                        sx={{ 
                          backgroundColor: lead.isValid ? 'inherit' : 'error.light',
                        }}
                      >
                        <TableCell>{lead.name}</TableCell>
                        <TableCell>{lead.email}</TableCell>
                        <TableCell>{lead.phone}</TableCell>
                        <TableCell>{lead.source}</TableCell>
                        <TableCell>{lead.status}</TableCell>
                        <TableCell>{product ? product.name : '-'}</TableCell>
                        <TableCell>
                          {lead.isValid ? 'Sim' : (
                            <Typography color="error" variant="caption">
                              {lead.errors.join(', ')}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {activeStep === 2 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" gutterBottom>
              Importação concluída!
            </Typography>
            <Typography variant="body1" paragraph>
              {success}
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleReset}
              sx={{ mt: 2 }}
            >
              Importar mais leads
            </Button>
          </Box>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {activeStep === 2 ? 'Fechar' : 'Cancelar'}
        </Button>
        {activeStep === 0 && (
          <Button 
            onClick={handleProcessFile} 
            variant="contained" 
            color="primary"
            disabled={!file || loading}
          >
            Processar Arquivo
          </Button>
        )}
        {activeStep === 1 && (
          <Button 
            onClick={handleImport} 
            variant="contained" 
            color="primary"
            disabled={loading || importedLeads.filter(l => l.isValid).length === 0}
          >
            Importar {importedLeads.filter(l => l.isValid).length} Leads
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LeadImporter; 